import { useState, useEffect } from "react";
import {
  BookOpen,
  Sparkles,
  Clock,
  BarChart3,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
} from "lucide-react";
import LogoCleanRebg from "../../assets/images/logo-clean-removebg.png";
import LogoClean from "../../assets/images/logo-clean.png";
import LessonPlanPrint from "../../../docs/img/planodeaula.png";
import ActivitiesPrint from "../../../docs/img/atividadesIA.png";
import AnalyticsPrint from "../../../docs/img/desempenho.png";

interface HomePageProps {
  onLoginClick: () => void;
}

export default function HomePage({ onLoginClick }: HomePageProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const features = [
    {
      title: "Planos de Aula com IA",
      description:
        "Gere planos de aula completos e personalizados em segundos. Nossa IA considera objetivos pedagógicos e o perfil da sua turma para criar conteúdo de alta qualidade.",
      icon: <BookOpen className="w-6 h-6" />,
      image: LessonPlanPrint,
      color: "#1967d2",
    },
    {
      title: "Criar atividades com IA",
      description:
        "Crie provas, simulados e listas de exercícios automaticamente com suporte à BNCC, economizando horas de planejamento e garantindo rigor pedagógico.",
      icon: <Sparkles className="w-6 h-6" />,
      image: ActivitiesPrint,
      color: "#d93025",
    },
    {
      title: "Análise de Desempenho",
      description:
        "Acompanhe o progresso de cada aluno com métricas inteligentes e relatórios automáticos que ajudam a identificar dificuldades precocemente.",
      icon: <BarChart3 className="w-6 h-6" />,
      image: AnalyticsPrint,
      color: "#188038",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % features.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [features.length]);

  return (
    <div className="min-h-screen bg-(--bg-primary)">
      <nav className="fixed top-0 w-full bg-(--header-bg) shadow-sm z-50 border-b border-(--border-color)">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img
                src={LogoCleanRebg}
                alt="Logo"
                className="h-15 sm:h-20 w-auto object-contain"
              />
              <span className="text-base sm:text-xl font-semibold text-(--text-secondary)">
                SIGA - Sistema Inteligente de Gestão Acadêmica
              </span>
            </div>

            <div className="hidden md:flex items-center gap-6">
              <a
                href="#recursos"
                className="font-medium hover:opacity-75 transition"
                style={{ color: "#5f6368" }}
              >
                Recursos
              </a>
              <a
                href="#como-funciona"
                className="font-medium hover:opacity-75 transition text-(--text-tertiary)"
              >
                Como Funciona
              </a>
              <a
                href="#beneficios"
                className="font-medium hover:opacity-75 transition text-(--text-tertiary)"
              >
                Benefícios
              </a>
              <button
                onClick={onLoginClick}
                className="px-6 py-2.5 rounded-lg font-medium text-white shadow-md hover:shadow-lg transition-all bg-(--primary-color)"
              >
                Fazer Login
              </button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              style={{ color: "#5f6368" }}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-(--header-bg) border-t border-(--border-color)">
            <div className="px-4 py-4 space-y-3">
              <a
                href="#recursos"
                className="block py-2 font-medium text-(--text-tertiary)"
              >
                Recursos
              </a>
              <a
                href="#como-funciona"
                className="block py-2 font-medium text-(--text-tertiary)"
              >
                Como Funciona
              </a>
              <a
                href="#beneficios"
                className="block py-2 font-medium text-(--text-tertiary)"
              >
                Benefícios
              </a>
              <button
                onClick={onLoginClick}
                className="w-full px-6 py-2.5 rounded-lg font-medium text-white"
                style={{ backgroundColor: "#1967d2" }}
              >
                Fazer Login
              </button>
            </div>
          </div>
        )}
      </nav>

      <section className="pt-32 pb-20 px-4 bg-(--bg-secondary)">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 border bg-(--card-bg) border-(--border-color)">
              <Sparkles className="w-4 h-4" style={{ color: "#1967d2" }} />
              <span className="text-sm font-medium text-(--text-secondary)">
                Educação impulsionada por IA
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight text-(--text-primary)">
              SIGA o seu Assistente{" "}
              <span className="text-(--primary-color)">Educacional</span>
            </h1>

            <p className="text-xl mb-8 leading-relaxed text-(--text-secondary)">
              Plataforma inovadora que utiliza inteligência artificial de ponta
              para transformar a forma como professores criam aulas, avaliam
              atividades e acompanham o progresso dos alunos.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onLoginClick}
                className="px-8 py-4 rounded-lg font-semibold text-lg text-white transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                style={{ backgroundColor: "#1967d2" }}
              >
                Começar Agora
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="recursos" className="py-20 px-4 bg-(--bg-primary)">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-(--text-primary)">
              Veja o SIGA em ação
            </h2>
            <p
              className="text-xl max-w-2xl mx-auto"
              style={{ color: "#5f6368" }}
            >
              Demonstrações interativas das principais funcionalidades da
              plataforma
            </p>
          </div>

          <div className="overflow-hidden">
            <div className="flex flex-col lg:flex-row min-h-125">
              <div className="w-full lg:w-2/5 p-8 lg:p-12 flex flex-col justify-between bg-(--bg-secondary)">
                <div>
                  <div className="space-y-4">
                    {features.map((feature, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveStep(index)}
                        className={`w-full text-left p-6 rounded-2xl transition-all duration-300 border-2 ${
                          activeStep === index
                            ? "bg-(--card-bg) border-blue-500 shadow-md transform scale-[1.02]"
                            : "border-transparent opacity-60 hover:opacity-100"
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div
                            className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-300`}
                            style={{
                              backgroundColor:
                                activeStep === index
                                  ? feature.color
                                  : "var(--bg-secondary)",
                              color:
                                activeStep === index
                                  ? "white"
                                  : "var(--text-secondary)",
                            }}
                          >
                            {feature.icon}
                          </div>
                          <div>
                            <h3
                              className="text-xl font-bold mb-1"
                              style={{
                                color:
                                  activeStep === index
                                    ? "var(--text-primary)"
                                    : "var(--text-secondary)",
                              }}
                            >
                              {feature.title}
                            </h3>
                            {activeStep === index && (
                              <p className="mt-3 leading-relaxed text-(--text-secondary) animate-in fade-in slide-in-from-top-2 duration-500">
                                {feature.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-8 flex gap-2">
                  {features.map((_, index) => (
                    <div
                      key={index}
                      className="h-1.5 flex-1 rounded-full bg-(--bg-accent) overflow-hidden"
                    >
                      {activeStep === index && (
                        <div className="h-full bg-blue-600 animate-progress" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="w-full lg:w-3/5 relative bg-(--bg-primary) flex items-center justify-center p-4 lg:p-8">
                <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-(--bg-secondary) border border-(--border-color) shadow-2xl group flex items-center justify-center">
                  {features[activeStep] && (
                    <img
                      key={activeStep}
                      src={features[activeStep].image}
                      alt={features[activeStep].title}
                      className="w-full h-full object-cover animate-in fade-in zoom-in duration-500"
                    />
                  )}

                  <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-(--bg-accent) rounded-2xl p-8 border border-(--border-color)">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: "#1967d2" }}
              >
                <Clock className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-semibold mb-2  text-(--text-primary)">
                  Economize até 70% do seu tempo
                </h3>
                <p className="text-lg text-(--text-secondary)">
                  Automatize tarefas repetitivas e foque no que realmente
                  importa: ensinar e inspirar seus alunos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="como-funciona" className="py-20 px-4 bg-(--bg-secondary)">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-(--text-primary)">
              Como funciona
            </h2>
            <p className="text-xl max-w-2xl mx-auto text-(--text-secondary)">
              Em apenas 3 passos simples, você estará pronto para revolucionar
              suas aulas
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-(--card-bg) p-8 rounded-xl shadow-sm border border-(--border-color)">
              <div
                className="w-16 h-16 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6"
                style={{ backgroundColor: "#1967d2" }}
              >
                1
              </div>
              <h3 className="text-xl font-semibold mb-3 text-(--text-primary)">
                Cadastre-se
              </h3>
              <p className="text-(--text-secondary)">
                Crie sua conta gratuitamente e configure seu perfil de professor
                em minutos
              </p>
            </div>

            <div className="text-center bg-(--card-bg) p-8 rounded-xl shadow-sm border border-(--border-color)">
              <div
                className="w-16 h-16 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6"
                style={{ backgroundColor: "#d93025" }}
              >
                2
              </div>
              <h3 className="text-xl font-semibold mb-3 text-(--text-primary)">
                Configure suas Turmas
              </h3>
              <p className="text-(--text-secondary)">
                Adicione suas turmas, disciplinas e defina os objetivos de
                aprendizagem
              </p>
            </div>

            <div className="text-center bg-(--card-bg) p-8 rounded-xl shadow-sm border border-(--border-color)">
              <div
                className="w-16 h-16 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6"
                style={{ backgroundColor: "#188038" }}
              >
                3
              </div>
              <h3 className="text-xl font-semibold mb-3 text-(--text-primary)">
                Comece a Usar
              </h3>
              <p className="text-(--text-secondary)">
                Explore todos os recursos e veja sua produtividade aumentar
                imediatamente
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="beneficios" className="py-20 px-4 bg-(--bg-primary)">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-(--text-primary)">
                Por que escolher o SIGA?
              </h2>
              <p className="text-xl mb-8 text-(--text-secondary)">
                Uma plataforma inovadora desenvolvida com tecnologia de ponta
                para revolucionar a educação brasileira
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <CheckCircle
                    className="w-6 h-6 shrink-0 mt-1"
                    style={{ color: "#188038" }}
                  />
                  <div>
                    <h4 className="font-semibold mb-1 text-(--text-primary)">
                      Baseado em Pedagogia
                    </h4>
                    <p className="text-(--text-secondary)">
                      Nossa IA foi treinada com metodologias pedagógicas
                      comprovadas
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <CheckCircle
                    className="w-6 h-6 shrink-0 mt-1"
                    style={{ color: "#188038" }}
                  />
                  <div>
                    <h4 className="font-semibold mb-1 text-(--text-primary)">
                      Alinhado à BNCC
                    </h4>
                    <p className="text-(--text-secondary)">
                      Conteúdos e atividades seguem a Base Nacional Comum
                      Curricular
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <CheckCircle
                    className="w-6 h-6 shrink-0 mt-1"
                    style={{ color: "#188038" }}
                  />
                  <div>
                    <h4 className="font-semibold mb-1 text-(--text-primary)">
                      Segurança de Dados
                    </h4>
                    <p className="text-(--text-secondary)">
                      Seus dados e dos alunos estão protegidos
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <CheckCircle
                    className="w-6 h-6 shrink-0 mt-1"
                    style={{ color: "#188038" }}
                  />
                  <div>
                    <h4 className="font-semibold mb-1 text-(--text-primary)">
                      Suporte Especializado
                    </h4>
                    <p className="text-(--text-secondary)">
                      Projeto bem Documentado e suporte ao usuário
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-12 rounded-2xl border bg-(--bg-accent) border-(--border-color)">
              <div className="text-center">
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: "#1967d2" }}
                >
                  <img
                    src={LogoClean}
                    alt="Logo"
                    className="w-22 h-22 rounded-full"
                  />
                </div>
                <h3 className="text-3xl font-bold mb-4 text-(--text-primary)">
                  Pronto para começar?
                </h3>
                <p className="text-lg mb-8 text-(--text-secondary)">
                  Faça parte da próxima geração de educadores que utilizam IA
                  para transformar o ensino
                </p>
                <button
                  onClick={onLoginClick}
                  className="px-8 py-4 rounded-lg font-semibold text-lg text-white transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2"
                  style={{ backgroundColor: "#1967d2" }}
                >
                  Acessar Plataforma
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer
        className="text-white py-12 px-4"
        style={{ backgroundColor: "#202124" }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div>
                  <img src={LogoClean} alt="Logo" className="w-8 h-8 " />
                </div>
                <span className="text-lg font-semibold">SIGA</span>
              </div>
              <p className="text-sm" style={{ color: "#9aa0a6" }}>
                Educação impulsionada por inteligência artificial
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">GitHub</h4>
              <ul className="space-y-2 text-sm" style={{ color: "#9aa0a6" }}>
                <li>
                  <a
                    href="https://github.com/Felipe-Silva7/assistente-educacional"
                    className="hover:text-white transition"
                    target="blank"
                  >
                    Repositorio com Documentação
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Autores</h4>
              <ul className="space-y-2 text-sm" style={{ color: "#9aa0a6" }}>
                <li>
                  <a
                    href="https://github.com/yMatheusFernandes"
                    className="hover:text-white transition"
                    target="blank"
                  >
                    Matheus Fernandes
                  </a>
                </li>
                <li>
                  <a
                    href="https://github.com/brennovic"
                    className="hover:text-white transition"
                    target="blank"
                  >
                    Brenno Victor
                  </a>
                </li>
                <li>
                  <a
                    href="https://github.com/Felipe-Silva7"
                    className="hover:text-white transition"
                    target="blank"
                  >
                    Felipe Silva
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t pt-8 text-center text-sm border-(--border-color)">
            <p>
              © {new Date().getFullYear()} SIGA. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
